import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { isNullOrUndefined } from '@sla-ng/core';
import { ChartData } from 'chart.js';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostUnitCost } from '../../../models/cost-unit-cost.model';
import { ICostUnitCostLayout } from '../../../models/layout/cost-unit-cost-layout.model';
import { ICostUnitCostState } from '../../../services/facades/cost-accounting/cost-unit-cost-state.model';
import { CostUnitCostFacade } from '../../../services/facades/cost-accounting/cost-unit-cost.facade';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { CostUnitCostLayoutService } from '../../../services/layout/cost-unit-cost-layout.service';


@Component({
    templateUrl: './cost-unit-cost-page.html',
    styleUrls: [ './cost-unit-cost-page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostUnitCostPage implements OnInit, OnDestroy {

    public costUnitCostState$: Observable<ICostUnitCostState>;
    public sharedDataState$: Observable<ISharedDataState>;
    public layout: ICostUnitCostLayout;
    public costAccounting: number;
    public activeTab$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    public costAccountingFormControl: FormControl<number> = new FormControl<number>(null);
    public costUnitFormControl: FormControl<number> = new FormControl<number>(null);

    public graphFilterFormGroup = this.myFormBuilder.group({
        measureUnit: null as number,
        costingLevel: null as number,
    });

    public costSum: number = 0;

    public costSumPerMeasureUnit = 0;

    public chartData: ChartData<'bar'> = {
        labels: [],
        datasets: [ { data: [] } ],
    };

    private destroy$: Subject<void> = new Subject<void>();
    // Holds the most recent List of CostUnitCosts
    private costList: BehaviorSubject<ICostUnitCost[]> = new BehaviorSubject<ICostUnitCost[]>([]);


    // For capturing changes of CostUnitCost for a single CostUnit
    private costUnitSelectionFormGroup: FormGroup = new FormGroup(
        {
            costAccounting: this.costAccountingFormControl,
            costUnit: this.costUnitFormControl,
        });
    // For capturing the filter for the graph
    private costingLevelSelectionFormGroup: FormGroup = new FormGroup(
        {
            costAccounting: this.costAccountingFormControl,
            graphFilter: this.graphFilterFormGroup,
        });

    constructor(protected myActivatedRoute: ActivatedRoute,
                protected myCostUnitCostFacade: CostUnitCostFacade,
                protected mySharedDataFacade: SharedDataFacade,
                protected myRouter: Router,
                protected myCostUnitCostLayoutService: CostUnitCostLayoutService,
                private myFormBuilder: FormBuilder) {
    }

    public ngOnInit(): void {

        this.layout = this.myCostUnitCostLayoutService.getCostUnitCostLayout();
        this.costUnitCostState$ = this.myCostUnitCostFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();

        // TODO: This should actually be handled by subscribing to the costUnitSelectionFormGroup
        this.costAccountingFormControl.valueChanges.subscribe((value) => {
            if (!isNullOrUndefined(value) && !isNullOrUndefined(this.costUnitFormControl.value)) {
                void this.myCostUnitCostFacade.fetchCostUnitCostingLevelCost(value, this.costUnitFormControl.value);
            }

            // Update Url with queryParams
            void this.myRouter.navigate([], {
                relativeTo: this.myActivatedRoute,
                queryParams: { costAccounting: value },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        });

        this.costingLevelSelectionFormGroup.valueChanges.pipe(
            filter((v: { costAccounting: number, graphFilter: { costingLevel: number | string, measureUnit: number | string } }) => !isNullOrUndefined(v.costAccounting)),
            map((v: { costAccounting: number, graphFilter: { costingLevel: number | string, measureUnit: number | string } }) => {
                if (v.graphFilter.costingLevel === '') {
                    this.graphFilterFormGroup.controls.costingLevel.patchValue(undefined, { emitEvent: false });
                    v.graphFilter.costingLevel = undefined;
                }
                if (v.graphFilter.measureUnit === '') {
                    this.graphFilterFormGroup.controls.measureUnit.patchValue(undefined, { emitEvent: false });
                    v.graphFilter.measureUnit = undefined;
                }
                return {
                    costAccounting: v.costAccounting,
                    graphFilter: { costingLevel: v.graphFilter.costingLevel, measureUnit: v.graphFilter.measureUnit },
                };
            }),
        ).subscribe(async (v: { costAccounting: number, graphFilter: { costingLevel: number, measureUnit: number } }) => {
            const cost: ICostUnitCost[] = await this.myCostUnitCostFacade.getCostUnitCostList(v.costAccounting, v.graphFilter.costingLevel, v.graphFilter.measureUnit);
            this.costList.next(cost);
        });

        this.costUnitSelectionFormGroup.valueChanges
            .pipe(
                filter((v: { costAccounting: number, costUnit: number }) => !isNullOrUndefined(v.costAccounting)))
            .subscribe((v: { costAccounting: number, costUnit: number }) => {

                if (!isNullOrUndefined(v.costUnit)) {
                    void this.myCostUnitCostFacade.fetchCostUnitCostingLevelCost(v.costAccounting, v.costUnit);
                }

                void this.myRouter.navigate([ '.' ], {
                    relativeTo: this.myActivatedRoute,
                    queryParams: { costAccounting: v.costAccounting },
                    replaceUrl: true,
                });

            });

        // Listen to query param changes
        this.myActivatedRoute.queryParams.subscribe((params: Params) => {
            if (params[ 'costAccounting' ]) {
                const costAccounting: number = Number(params[ 'costAccounting' ]);
                if (!Number.isNaN(costAccounting)) {
                    this.costAccountingFormControl.patchValue(costAccounting);
                }
            }
            if (params[ 'type' ]) {
                const type = params[ 'type' ] as string;
                if (type === 'costUnitCost') {
                    this.activeTab$.next(0);
                }
                if (type === 'breakEven') {
                    this.activeTab$.next(1);
                }
            }
        });

        // Listen to tab-changes
        this.activeTab$.subscribe((tabIndex) => {
            const type: string = tabIndex === 0 ? 'costUnitCost' : 'breakEven';
            void this.myRouter.navigate([ '.' ], {
                relativeTo: this.myActivatedRoute,
                queryParams: { type },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        });

        // Calculate the cost sum
        combineLatest([
            this.sharedDataState$.pipe(map((s: ISharedDataState) => s.costingLevelTree)),
            this.costUnitCostState$.pipe(map((state: ICostUnitCostState) => state.costUnitCostingLevelCostMap)),
        ])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([ costingLevelTree, costUnitCostingLevelCostMap ]) => {
                let sumCost: number = 0;
                let sumCostPerMeasureUnit: number = 0;
                for (const rootCostingLevel of costingLevelTree) {
                    if (!isNullOrUndefined(costUnitCostingLevelCostMap[ rootCostingLevel.id ])) {
                        sumCost += costUnitCostingLevelCostMap[ rootCostingLevel.id ].costCt;
                        sumCostPerMeasureUnit += costUnitCostingLevelCostMap[ rootCostingLevel.id ].costCtPerMeasureUnit;
                    }
                }
                this.costSum = sumCost;
                this.costSumPerMeasureUnit = sumCostPerMeasureUnit;
            });

        this.buildChartData();

        void firstValueFrom(this.mySharedDataFacade.fetchCostingLevels());
        void firstValueFrom(this.mySharedDataFacade.fetchCostUnits());
        void firstValueFrom(this.mySharedDataFacade.fetchMeasureUnits());
    }

    public onChangeTab(activeTab: number): void {
        this.activeTab$.next(activeTab);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.myCostUnitCostFacade.clearState();
    }

    /**
     * Updates the chart's data if either the dataset or the CostUnit-Lookup changes
     * @private
     */
    private buildChartData(): void {
        combineLatest([
            this.sharedDataState$.pipe(map((s: ISharedDataState) => s.costUnitLookup)),
            this.costList,
        ])
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe(([ costUnitLookup, costList ]) => {
                const labels: string[] = [];
                const data: number[] = [];
                for (const cost of costList) {

                    const costEuro: number = cost.costCt / 100;
                    let label: string;
                    if (!isNullOrUndefined(costUnitLookup[ cost.costUnitId ])) {
                        label = `${costUnitLookup[ cost.costUnitId ].articleNr} - ${costUnitLookup[ cost.costUnitId ].name}`;
                    }
                    labels.push(label);
                    data.push(costEuro);
                }
                this.chartData = {
                    labels, datasets: [ { data } ],
                };
            });
    }
}
